import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { CalendarMonthEventPill } from './calendar-event-block'
import CalendarBookSlotIcon from './calendar-book-slot-icon'
import {
  buildBookStudentUrl,
  getBookingEventKey,
  getMonthGridDays,
  isCurrentMonth,
  isPastBookSlot,
  isToday,
  MONTH_VISIBLE_EVENTS,
} from './calendar-utils'
import useCalendarStyles from './calendar-styles'

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const CalendarMonthView = ({
  anchorDate,
  bookingsByDate,
  canBookStudent,
  bookingUrl,
  onBookSlotClick,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useCalendarStyles()
  const days = getMonthGridDays(anchorDate)

  return (
    <>
      <Box className={classes.calendarHeader}>
        <Typography className={classes.calendarTitle}>
          {anchorDate.toFormat('LLLL yyyy')}
        </Typography>
      </Box>

      <Box className={classes.monthGrid}>
        {WEEKDAY_HEADERS.map(day => (
          <Typography key={day} className={classes.monthWeekdayHeader}>
            {day}
          </Typography>
        ))}

        {days.map(day => {
          const dateKey = day.toISODate()
          const dayBookings = bookingsByDate[dateKey] || []
          const visibleBookings = dayBookings.slice(0, MONTH_VISIBLE_EVENTS)
          const hiddenCount = dayBookings.length - visibleBookings.length
          const dayIsToday = isToday(day)
          const inCurrentMonth = isCurrentMonth(day, anchorDate)
          const isBookable =
            canBookStudent && bookingUrl && !isPastBookSlot(day)
          const bookUrl = isBookable
            ? buildBookStudentUrl(bookingUrl, day)
            : null
          const dayCellClassName = [
            classes.monthDayCell,
            !inCurrentMonth ? classes.monthDayOutside : '',
            dayIsToday ? classes.monthDayToday : '',
            isBookable ? classes.monthDayCellBookable : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <Box key={dateKey} className={dayCellClassName}>
              <span className={classes.monthDayNumber}>{day.day}</span>
              {visibleBookings.map(booking => (
                <CalendarMonthEventPill
                  key={getBookingEventKey(booking)}
                  booking={booking}
                  onReschedule={onReschedule}
                  onPayment={onPayment}
                  onCancellation={onCancellation}
                />
              ))}
              {hiddenCount > 0 ? (
                <Typography
                  component="span"
                  className={classes.monthMoreButton}
                >
                  +{hiddenCount} more
                </Typography>
              ) : null}
              {isBookable ? (
                <Box
                  component="a"
                  href={bookUrl}
                  className={`${classes.monthBookSlotTarget} ${classes.monthBookSlotTargetLink}`}
                  onClick={onBookSlotClick}
                  aria-label={`Book a student on ${day.toFormat(
                    'cccc, LLLL d',
                  )}`}
                >
                  <CalendarBookSlotIcon className={classes.monthBookSlotIcon} />
                </Box>
              ) : null}
            </Box>
          )
        })}
      </Box>
    </>
  )
}

CalendarMonthView.propTypes = {
  anchorDate: PropTypes.object.isRequired,
  bookingsByDate: PropTypes.object.isRequired,
  canBookStudent: PropTypes.bool,
  bookingUrl: PropTypes.string,
  onBookSlotClick: PropTypes.func,
  onReschedule: PropTypes.func,
  onPayment: PropTypes.func,
  onCancellation: PropTypes.func,
}

export default CalendarMonthView
